import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPlus, FiFileText, FiLock } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  getPaymentVouchers, cancelPaymentVoucher,
  getDonationCategories,
  sendVoucherEmail,
} from '../../services/api';
import { PageHeader, CancelConfirmModal } from '../../components/common';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { usePdfDownload } from '../../pdf/usePdfDownload';
import { TOAST, ACTIONS, MODAL, NAV } from '../../constants/labels';
import { isOpeningFinancialYear } from '../../utils/financialYears';
import { EXPENSES_LABEL } from './paymentsConfig';
import PaymentsSummaryCards from './PaymentsSummaryCards';
import PaymentsFilters from './PaymentsFilters';
import PaymentsTable from './PaymentsTable';

const PaymentsPage = () => {
  const {
    activeDerasarId,
    activeDerasar,
    financialYears,
    visibleFinancialYears,
    activeFinancialYearId,
    setActiveFinancialYearId,
    can,
  } = useAuth();
  const navigate = useNavigate();

  const [vouchers, setVouchers]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);

  /* filters — all client-side */
  const [filterYear, setFilterYear]     = useState('');
  const [filterCat, setFilterCat]       = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  const loadMeta = useCallback(async () => {
    if (!activeDerasarId) return;
    try {
      const catRes = await getDonationCategories(activeDerasarId);
      setCategories(Array.isArray(catRes.data) ? catRes.data : []);
    } catch {
      // categories are display-only — silent failure is acceptable
    }
  }, [activeDerasarId]);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const params = { derasarId: activeDerasarId };
      if (filterYear) params.financialYearId = filterYear;
      if (filterCat) params.categoryId = filterCat;
      if (filterFromDate) params.fromDate = filterFromDate;
      if (filterToDate) params.toDate = filterToDate;
      const vRes = await getPaymentVouchers(params);
      setVouchers(Array.isArray(vRes.data) ? vRes.data : []);
    } catch {
      toast.error(TOAST.loadFailed(EXPENSES_LABEL));
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId, filterYear, filterCat, filterFromDate, filterToDate]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!activeDerasarId) return;
    setFilterYear(activeFinancialYearId || '');
  }, [activeDerasarId, activeFinancialYearId]);

  const filterYearOptions = useMemo(
    () => (financialYears || []).filter((financialYear) => !isOpeningFinancialYear(financialYear)),
    [financialYears],
  );

  const selectedFilterYear = useMemo(
    () => filterYearOptions.find((financialYear) => String(financialYear.id) === String(filterYear)) || null,
    [filterYearOptions, filterYear],
  );

  const filterMinDate = selectedFilterYear?.startDate?.slice(0, 10) || '';
  const filterMaxDate = selectedFilterYear?.endDate?.slice(0, 10) || '';

  useEffect(() => {
    if (!filterYear) {
      setFilterFromDate('');
      setFilterToDate('');
      return;
    }

    setFilterFromDate(filterMinDate || '');
    setFilterToDate(filterMaxDate || '');
  }, [filterYear, filterMinDate, filterMaxDate]);

  /* -- cancel hook ------------------------------------------------------ */
  const { cancelModal, cancelReason, cancelling, openCancel, closeCancel, setReason, handleCancel } =
    useCancelConfirm(cancelPaymentVoucher, load, TOAST.cancelSuccess(EXPENSES_LABEL));

  /* -- PDF / email hook ------------------------------------------------- */
  const { download: downloadVoucher } = usePdfDownload('paymentVoucher');

  const handleVoucherDownload = useCallback((p) => {
    downloadVoucher({ voucher: p, derasar: activeDerasar?.derasarId, journalLines: p.journalLines || [] });
  }, [downloadVoucher, activeDerasar]);

  const handleSendEmail = useCallback(async (v) => {
    try {
      const res = await sendVoucherEmail(v.id);
      toast.success(res.data?.message || 'Email sent successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send email');
    }
  }, []);

  /* -- clear all filters ------------------------------------------------ */
  const clearFilters = () => {
    setFilterYear('');
    setFilterCat('');
    setFilterSearch('');
    setFilterFromDate('');
    setFilterToDate('');
  };

  const visibleVouchers = useMemo(() => {
    if (!filterSearch.trim()) return vouchers;
    const q = filterSearch.trim().toLowerCase();
    return vouchers.filter((v) =>
      v.voucherNo?.toLowerCase().includes(q) ||
      v.payeeName?.toLowerCase().includes(q) ||
      v.narration?.toLowerCase().includes(q)
    );
  }, [vouchers, filterSearch]);

  const totalAmount = visibleVouchers.reduce((s, v) => s + parseFloat(v.amount || 0), 0);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        icon={<FiFileText />}
        title={NAV.paymentVouchers}
        actions={
          can('payments', 'manage') && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate('/accounting/payments/new')}
              disabled={!!selectedFilterYear?.isClosed}
              title={selectedFilterYear?.isClosed ? 'This financial year is locked — unlock it before adding new payments.' : undefined}
            >
              {selectedFilterYear?.isClosed ? <FiLock /> : <FiPlus />} {ACTIONS.new(EXPENSES_LABEL)}
            </button>
          )
        }
      />

      <PaymentsSummaryCards vouchers={visibleVouchers} />

      <PaymentsFilters
        years={filterYearOptions}
        categories={categories}
        filterYear={filterYear}
        filterCat={filterCat}
        filterSearch={filterSearch}
        filterFromDate={filterFromDate}
        filterToDate={filterToDate}
        minDate={filterMinDate}
        maxDate={filterMaxDate}
        onYearChange={(nextValue) => {
          setFilterYear(nextValue);
          if (nextValue) setActiveFinancialYearId(nextValue);
        }}
        onCatChange={setFilterCat}
        onSearchChange={setFilterSearch}
        onFromDateChange={setFilterFromDate}
        onToDateChange={setFilterToDate}
        onClearFilters={clearFilters}
      />

      <PaymentsTable
        vouchers={visibleVouchers}
        totalAmount={totalAmount}
        onDownload={handleVoucherDownload}
        onEmail={handleSendEmail}
        onEditClick={(v) => navigate(`/accounting/payments/${v.id}/edit`)}
        onCancelClick={openCancel}
        canManage={can('payments', 'manage')}
      />

      <CancelConfirmModal
        entity={EXPENSES_LABEL}
        item={cancelModal}
        itemDisplay={cancelModal?.voucherNo}
        amount={cancelModal?.amount}
        extraNote={MODAL.cancel.journalEntryNote}
        reason={cancelReason}
        onReasonChange={setReason}
        onConfirm={handleCancel}
        onClose={closeCancel}
        cancelling={cancelling}
      />
    </div>
  );
};

export default PaymentsPage;
